// 与 8789 后端的 API 客户端

import { notifyCreditInsufficient } from '@/components/ui/toast';

const TOKEN_KEY = 'milli_ai_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export async function api<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`/api${path}`, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    // 仅在"确实持有 token"时（会话过期）才清空并跳登录页；
    // 从未登录（无 token）时不跳转，避免 /login 上的无限重载
    if (token) {
      clearToken();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    throw new Error('未登录或登录已过期');
  }

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    // 积分不足（402）→ 全局通知用户，而不是静默失败
    if (res.status === 402) {
      const detail = data?.detail || data?.error || '积分不足，无法完成操作';
      notifyCreditInsufficient(String(detail));
    }
    throw new Error(data?.detail || data?.error || `请求失败 (${res.status})`);
  }
  return data as T;
}

// ─── 认证 ───────────────────────────────────────────────────────

export async function login(username: string, password: string) {
  const data = await api<{ ok: boolean; user: any; token: string }>(
    '/auth/login',
    { method: 'POST', body: JSON.stringify({ username, password }) }
  );
  setToken(data.token);
  return data.user;
}

export async function register(body: {
  username: string;
  password: string;
  displayName: string;
  invitationCode: string;
}) {
  const data = await api<{ ok: boolean; user: any; token: string }>(
    '/auth/register',
    { method: 'POST', body: JSON.stringify(body) }
  );
  setToken(data.token);
  return data.user;
}

export async function getMe() {
  return api<{ ok: boolean; user: any }>('/auth/me');
}

export async function logout() {
  try {
    await api('/auth/logout', { method: 'POST' });
  } finally {
    clearToken();
  }
}

// ─── 仪表盘 ─────────────────────────────────────────────────────

export async function getDashboardSummary() {
  return api('/dashboard/summary');
}

export async function getBalanceSummary() {
  return api<{ totalCnyBalance: number; displayTotalCnyBalance: string; connectedCount: number; platforms: any[] }>(
    '/model-platforms/balance-summary'
  );
}

// ─── 管理员 ─────────────────────────────────────────────────────

export async function listInvitationCodes() {
  return api<{ ok: boolean; codes: any[] }>('/admin/invitation-codes');
}

export async function generateInvitationCodes(count: number) {
  return api<{ ok: boolean; codes: { code: string; expiresAt: string }[]; count: number }>(
    '/admin/invitation-codes',
    { method: 'POST', body: JSON.stringify({ count }) }
  );
}

// ─── 社媒监控 ──────────────────────────────────────────────────

export async function listAccounts() {
  return api<{ accounts: any[] }>('/media/accounts');
}

export async function verifyAccount(platform: string, uid: string) {
  return api<{ status: string; uid: string; info: any }>(
    `/media/verify?platform=${encodeURIComponent(platform)}&uid=${encodeURIComponent(uid)}`
  );
}

export async function addAccount(platform: string, uid: string, info?: any) {
  return api<{ ok: boolean; account: any }>(
    '/media/accounts',
    { method: 'POST', body: JSON.stringify({ platform, uid, info }) }
  );
}

export async function deleteAccount(accountId: string) {
  return api(`/media/accounts/${accountId}`, { method: 'DELETE' });
}

export async function checkAccount(accountId: string) {
  return api<{ status: string; account_id: number }>(
    `/media/accounts/${accountId}/check`,
    { method: 'POST' }
  );
}

export async function getLibrary(params?: { page?: number; page_size?: number; platform?: string; sort?: string; order?: string; user_id?: number; tag?: string }) {
  const q = new URLSearchParams();
  if (params?.page) q.set('page', String(params.page));
  if (params?.page_size) q.set('page_size', String(params.page_size));
  if (params?.platform) q.set('platform', params.platform);
  if (params?.sort) q.set('sort', params.sort);
  if (params?.order) q.set('order', params.order);
  if (params?.user_id) q.set('user_id', String(params.user_id));
  if (params?.tag) q.set('tag', params.tag);
  const qs = q.toString();
  return api<{ items: any[]; total: number; page: number; page_size: number }>(`/media/library${qs ? '?' + qs : ''}`);
}

export async function extractContent(videoUrl: string, platform: string) {
  return api<{ status: string; id: string; already: boolean; updated?: boolean; description?: string }>('/media/extract', {
    method: 'POST',
    body: JSON.stringify({ url: videoUrl, platform }),
  });
}

export async function transcribeVideo(videoId: string, userId?: number) {
  const q = userId ? `?user_id=${userId}` : '';
  return api<{ status: string; id: string }>(`/media/transcribe/${videoId}${q}`);
}

export async function transcribeStatus(videoId: string, userId?: number) {
  const q = userId ? `?user_id=${userId}` : '';
  return api<{ video_id: string; transcribe_status: string; transcript: string; transcribe_error: string }>(
    `/media/transcribe/status/${videoId}${q}`
  );
}

// ─── 人设管理 ──────────────────────────────────────────────────

export async function getPersona() {
  return api<{ nickname: string; uid: string; profile: any; video_count: number; total_digg: number; avg_digg: number; top_hashtags: {tag: string; count: number}[]; account_keywords: {word: string; count: number}[]; recent_topics: any[]; expression: string; memory: string; rewrite_config: any; analyzed_at: string | null; library_match: { matched: number; transcribed: number }; library_tags: {tag: string; count: number}[]; dimensions: Record<string, any> }>('/persona');
}

export async function saveExpression(expression: string) {
  return api('/expression', {
    method: 'POST',
    body: JSON.stringify({ expression }),
  });
}

export async function saveMemory(memory: string) {
  return api('/memory', {
    method: 'POST',
    body: JSON.stringify({ memory }),
  });
}

export async function getMemory() {
  return api<{ memory: string }>('/memory');
}

export async function getExpression() {
  return api<{ expression: string }>('/expression');
}

export async function importPersona(url?: string) {
  return api<{ status: string; message: string; persona?: any; extract_job_id?: string }>('/persona/import', {
    method: 'POST',
    body: JSON.stringify({ url: url || '' }),
  });
}

export async function importPersonaFromDiagnosis(runId: number) {
  return api<{ status: string; message: string; persona?: any; extract_job_id?: string }>('/persona/import-from-diagnosis', {
    method: 'POST',
    body: JSON.stringify({ run_id: runId }),
  });
}

export async function extractExpression() {
  return api<{ status: string; job_id: string }>('/expression/extract', {
    method: 'POST',
  });
}

export async function extractExpressionStatus(jobId: string) {
  return api<{
    status: string;
    step?: string;
    message?: string;
    videos_total?: number;
    videos_done?: number;
    result?: string;
    error?: string;
    code?: string;
    need_total?: number;
    have_transcript?: number;
  }>(`/expression/extract/status?job_id=${encodeURIComponent(jobId)}`);
}

export async function transcribeAllExpression() {
  return api<{ status: string; job_id: string }>('/expression/transcribe-all', {
    method: 'POST',
  });
}

export async function transcribeAllExpressionStatus(jobId: string) {
  return api<{
    status: string;
    step?: string;
    message?: string;
    videos_total?: number;
    videos_done?: number;
    error?: string;
    followup_job_id?: string;
  }>(`/expression/transcribe-all/status?job_id=${encodeURIComponent(jobId)}`);
}

export async function clearPersona() {
  return api<{ status: string; message: string }>('/persona', { method: 'DELETE' });
}

// ─── 内容中心 ──────────────────────────────────────────────────

export type HotspotChannelSource = {
  name: string;
  url: string;
  type?: string;
};

export type HotspotChannel = {
  id: number;
  name: string;
  sources: HotspotChannelSource[];
  keywords: string[];
  blacklist: string[];
  enabled: boolean;
  sortOrder: number;
  category?: string;
  badge?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type HotspotChannelPayload = {
  name: string;
  sources: HotspotChannelSource[];
  keywords: string[];
  blacklist: string[];
  enabled?: boolean;
};

export async function getHotspots(channelId?: number, limit = 10, force = false) {
  const q = channelId
    ? `?channel_id=${channelId}&limit=${limit}${force ? '&force=true' : ''}`
    : `?limit=${limit}${force ? '&force=true' : ''}`;
  return api<{
    channel?: { id: number; name: string } | null;
    items: any[];
    fallback?: boolean;
    sourceUrl?: string;
    updatedAt?: string;
    sources?: { name: string; url: string; ok: boolean; error?: string }[];
    error?: string;
  }>(`/content/hotspots${q}`);
}

export async function getHotspotChannels() {
  return api<{ items: HotspotChannel[] }>('/content/hotspot-channels');
}

export type HotspotTemplate = {
  name: string;
  sources?: HotspotChannelSource[];
  keywords?: string[];
  added?: boolean;
};

export async function getHotspotTemplates() {
  return api<{ groups: Record<string, HotspotTemplate[]> }>('/content/hotspot-templates');
}

export type HotspotSourceItem = {
  group: string;
  name: string;
  url: string;
  type: string;
};

export async function getHotspotSources() {
  return api<{ items: HotspotSourceItem[] }>('/content/hotspot-sources');
}

// ─── 管理员：热点管理（系统设置 → 热点管理） ───────────────────

export async function getAdminHotspotChannels() {
  return api<{ items: HotspotChannel[]; templates: Record<string, HotspotTemplate[]> }>(
    '/admin/hotspot-channels'
  );
}

export async function getAdminHotspotSources() {
  return api<{ items: HotspotSourceItem[] }>('/admin/hotspot-sources');
}

export async function createAdminHotspotChannel(payload: HotspotChannelPayload) {
  return api<{ ok: boolean; channel: HotspotChannel }>('/admin/hotspot-channels', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateAdminHotspotChannel(channelId: number, payload: HotspotChannelPayload) {
  return api<{ ok: boolean; channel: HotspotChannel }>(`/admin/hotspot-channels/${channelId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteAdminHotspotChannel(channelId: number) {
  return api<{ ok: boolean }>(`/admin/hotspot-channels/${channelId}`, { method: 'DELETE' });
}

export async function createHotspotChannel(payload: HotspotChannelPayload) {
  return api<{ ok: boolean; channel: HotspotChannel }>('/content/hotspot-channels', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateHotspotChannel(channelId: number, payload: HotspotChannelPayload) {
  return api<{ ok: boolean; channel: HotspotChannel }>(`/content/hotspot-channels/${channelId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteHotspotChannel(channelId: number) {
  return api<{ ok: boolean }>(`/content/hotspot-channels/${channelId}`, { method: 'DELETE' });
}

export async function getViralTopics(limit = 10) {
  return api<{
    ok: boolean;
    items: any[];
    sourceSummary: { libraryCount: number; ipMaterialCount: number; leadCount: number; hotspotCount: number };
    scoringWeights: any;
  }>(`/content/viral-topics?limit=${limit}`);
}

export async function submitIpMaterial(payload: {
  type: 'audio' | 'text';
  payload: { text?: string; fileName?: string; note?: string };
}) {
  return api<{ ok: boolean; id?: number; type?: string; status: string }>('/content/ip-material', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getIpMaterials(limit = 50) {
  return api<{ items: any[]; total: number }>(`/content/ip-materials?limit=${limit}`);
}

export async function generateTranscript(duration: string, topic: string) {
  return api<{ ok: boolean; topic: string; duration: string; estimatedChars: number; status: string; draft: string }>(
    '/content/transcript',
    { method: 'POST', body: JSON.stringify({ duration, topic }) }
  );
}

export async function startWechatArticle() {
  return api<{ ok: boolean; targetAgent: string; status: string }>('/content/wechat-article', {
    method: 'POST',
  });
}

export async function requestInterview(payload: { topic: string; mode: string; questions: string[] }) {
  return api<{ ok: boolean; id?: number; status: string; interviewKey?: string; questions?: string[] }>(
    '/content/interview',
    { method: 'POST', body: JSON.stringify(payload) }
  );
}

export async function rewriteContent(text: string, style?: string) {
  return api<{ ok: boolean; result: string }>('/content/rewrite-text', {
    method: 'POST',
    body: JSON.stringify({ text, style: style || 'casual' }),
  });
}

// 内容库异步改写（基于逐字稿，走后台任务 + 轮询）；userId 供 admin 对目标用户内容改写
export async function startRewrite(videoId: string, prompt?: string, userId?: number) {
  return api<{ status: string; job_id: string }>('/content/rewrite', {
    method: 'POST',
    body: JSON.stringify({ video_id: videoId, prompt: prompt || '', user_id: userId || 0 }),
  });
}

export async function getRewriteStatus(jobId: string) {
  return api<{ status: string; step?: string; message?: string; result?: string; originality?: number; error?: string | null }>(
    `/rewrite/status?job_id=${encodeURIComponent(jobId)}`
  );
}

// 改写参数配置（内容撰写设置，存 persona.rewrite_config）
export async function getRewriteConfig() {
  return api<Record<string, any>>('/config/rewrite');
}

export async function saveRewriteConfig(config: Record<string, any>) {
  return api<{ status: string }>('/config/rewrite', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

// ─── 海报生成 ──────────────────────────────────────────────────

export async function uploadImage(file: File) {
  const form = new FormData();
  form.append('file', file);
  const token = getToken();
  const res = await fetch('/api/content/upload-image', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.detail || `上传失败 (${res.status})`);
  }
  return res.json() as Promise<{ ok: boolean; url: string; filename: string }>;
}

export async function generatePoster(body: {
  text: string;
  subtitle?: string;
  size?: string;
  template_id?: string;
  scheme?: string;
  image_urls?: Record<string, string>;
}) {
  return api<{ ok: boolean; image_url: string; scheme: string; template_id: string }>('/content/generate-poster', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getPosterTemplates() {
  return api<{ items: Array<{ id: string; name: string; description: string; category: string; thumbnail: string; sizes: string[]; default_size: string; image_elements?: Array<{id: string; field: string; label: string; optional: boolean}> }> }>('/content/poster-templates');
}

export async function getPosters(limit = 20) {
  return api<{ items: any[]; total: number }>(`/content/posters?limit=${limit}`);
}

export async function getSeedreamConfig() {
  return api<{ config: { provider: string; model: string; base_url: string; api_key: string } }>('/config/seedream');
}

export async function saveSeedreamConfig(config: Record<string, string>) {
  return api<{ status: string }>('/config/seedream', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

// ─── 模板管理（后台） ──────────────────────────────────────────

export async function getPosterTemplateDetail(id: string) {
  return api<{ ok: boolean; template: any }>(`/admin/poster-templates/${encodeURIComponent(id)}`);
}

export async function savePosterTemplate(id: string, data: any) {
  return api<{ ok: boolean; template_id: string; message: string }>(
    `/admin/poster-templates/${encodeURIComponent(id)}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function deletePosterTemplate(id: string) {
  return api<{ ok: boolean; message: string }>(
    `/admin/poster-templates/${encodeURIComponent(id)}`,
    { method: 'DELETE' }
  );
}

export async function getInterviewOutline(topic: string, mode = '一般采访') {
  return api<{ ok: boolean; topic: string; mode: string; questionCount: number; questions: string[] }>(
    '/content/interview-outline',
    { method: 'POST', body: JSON.stringify({ topic, mode }) }
  );
}

// ─── 后台管理 ──────────────────────────────────────────────────

export async function listUsers() {
  return api<{ ok: boolean; users: any[] }>('/admin/users');
}

export async function toggleUser(username: string, disabled: boolean) {
  return api(`/admin/users/${username}`, {
    method: 'PUT',
    body: JSON.stringify({ disabled }),
  });
}

export async function deleteUser(username: string) {
  return api(`/admin/users/${username}`, { method: 'DELETE' });
}

export async function resetUserPassword(username: string, password: string) {
  return api<{ ok: boolean; message: string }>(
    `/admin/users/${encodeURIComponent(username)}/reset-password`,
    { method: 'POST', body: JSON.stringify({ password }) }
  );
}

export async function adjustUserCredit(username: string, amount: number, reason?: string) {
  return api<{ ok: boolean; balance: number; message: string }>(
    `/admin/users/${encodeURIComponent(username)}/credit`,
    { method: 'POST', body: JSON.stringify({ amount, reason: reason || '' }) }
  );
}

export async function getUsage() {
  return api<{ ok: boolean; month: string; users: any[] }>('/admin/usage');
}

export async function getAdminUserStats() {
  return api<{ ok: boolean; month: string; summary: any; users: any[] }>('/admin/user-stats');
}

export async function getLoginLogs(username: string) {
  return api<{ ok: boolean; username: string; logs: any[] }>(
    `/admin/users/${encodeURIComponent(username)}/login-logs`
  );
}

// ─── 通知系统 ──────────────────────────────────────────────────

export async function getNotifications(limit = 50) {
  return api<{ ok: boolean; items: any[]; unread: number }>(
    `/notifications?limit=${limit}`
  );
}

export async function getUnreadCount() {
  return api<{ ok: boolean; unread: number }>('/notifications/unread-count');
}

export async function markNotificationRead(id: number) {
  return api(`/notifications/${id}/read`, { method: 'POST' });
}

export async function markAllNotificationsRead() {
  return api('/notifications/read-all', { method: 'POST' });
}

export async function listNotificationTemplates() {
  return api<{ ok: boolean; items: any[] }>('/admin/notification-templates');
}

export async function saveNotificationTemplate(data: {
  template_key: string;
  name: string;
  title_template: string;
  content_template: string;
  is_active: boolean;
}) {
  return api(
    `/admin/notification-templates/${encodeURIComponent(data.template_key)}`,
    { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function deleteNotificationTemplate(templateKey: string) {
  return api(`/admin/notification-templates/${encodeURIComponent(templateKey)}`, { method: 'DELETE' });
}

export async function adminSendNotification(payload: {
  username?: string;
  title: string;
  content: string;
}) {
  return api<{ ok: boolean; count: number; message: string }>(
    '/admin/notifications/send',
    { method: 'POST', body: JSON.stringify(payload) }
  );
}

// ─── 标签系统 ──────────────────────────────────────────────────

export async function listTags() {
  return api<{ tags: any[] }>('/media/tags');
}

export async function createTag(name: string, color?: string) {
  return api<{ status: string; id: number }>('/media/tags', {
    method: 'POST',
    body: JSON.stringify({ name, color }),
  });
}

export async function updateTag(tagId: number, data: { name?: string; color?: string }) {
  return api(`/media/tags/${tagId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteTag(tagId: number) {
  return api(`/media/tags/${tagId}`, { method: 'DELETE' });
}

export async function setLibraryTags(libraryId: number, tagIds: number[]) {
  return api(`/media/library/${libraryId}/tags`, {
    method: 'POST',
    body: JSON.stringify({ tag_ids: tagIds }),
  });
}

// ─── 用户权限管理 ──────────────────────────────────────────────

export async function updateUserPermissions(username: string, permissions: any) {
  return api(`/admin/users/${encodeURIComponent(username)}/permissions`, {
    method: 'PUT',
    body: JSON.stringify({ permissions }),
  });
}

// ─── 模型配置 ──────────────────────────────────────────────────

export async function getLlmConfig() {
  return api<{ config: any; providers: any }>('/config/llm');
}

export async function saveLlmConfig(data: any) {
  return api('/config/llm', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAsrConfig() {
  return api<{ config: any; providers: any }>('/config/asr');
}

export async function saveAsrConfig(data: any) {
  return api('/config/asr', {
    method: 'POST',
    body: JSON.stringify({ asr: data }),
  });
}

export async function testAsrConnection(provider: string) {
  return api<{ status: string; message: string }>(
    `/config/asr/test?provider=${encodeURIComponent(provider)}`
  );
}

// ─── 监控配置（定时监控） ─────────────────────────────────────────

export type MonitorConfig = {
  enabled: boolean;
  times_per_day: number;
  time_points: string[];
  /** 单次监控单价（后端实时读 credit_pricing，供前端预估每日消耗成本） */
  credit_rates?: {
    video: number;
    other: number;
  };
};

export async function getMonitorConfig() {
  return api<MonitorConfig>('/config/monitor');
}

export async function saveMonitorConfig(data: MonitorConfig) {
  return api('/config/monitor', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ─── TikHub 配置 ────────────────────────────────────────────────

export async function getTikhubConfig() {
  return api<{ config: { api_key?: string } }>('/config/tikhub');
}

export async function saveTikhubConfig(data: { tikhub: { api_key: string } }) {
  return api('/config/tikhub', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ─── 积分定价管理 ────────────────────────────────────────────────

export type CreditPricingItem = {
  id: number;
  operation_key: string;
  display_name: string;
  platform: string;
  unit: string;
  cost_source: string;
  cost_currency: string;
  cost_value: number;
  credit_cost: number;
  price_cny: number;
  margin_pct: number;
  is_active: number;
  updated_at: string;
};

export type CreditPricingResponse = {
  ok: boolean;
  credit_cny_per_point: number;
  usd_to_cny: number;
  items: CreditPricingItem[];
};

export async function getCreditPricing() {
  return api<CreditPricingResponse>('/admin/credit/pricing');
}

export async function updateCreditPricing(operation_key: string, data: { credit_cost: number; cost_value?: number; is_active?: boolean }) {
  return api<{ ok: boolean; item: CreditPricingItem }>(`/admin/credit/pricing/${operation_key}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function refreshCreditPricing() {
  return api<{ ok: boolean; fetched_at: string; sources: any; updates: any[] }>('/admin/credit/pricing/refresh', {
    method: 'POST',
  });
}

// ─── 小红书账号体检 ──────────────────────────────────────────────

export type XhsQuoteRequest = {
  profile_url: string;
  primary_goal: string;
  intended_audience?: string;
  intended_positioning?: string;
  product_offer?: string;
};

export type XhsTier = {
  tier: 'basic' | 'enhanced' | 'deep';
  name: string;
  credit_cost: number;
  price_cny: number;
  estimated_tikhub_usd: number;
  estimated_tikhub_cny: number;
  calls: Record<string, { count: number; unit_usd: number; subtotal_usd: number }>;
  description: string;
};

export type XhsQuoteResponse = {
  ok: boolean;
  xhs_user_id: string;
  profile_url: string;
  tiers: XhsTier[];
  ruleset_version: string;
  note: string;
};

export async function xhsQuote(body: XhsQuoteRequest) {
  return api<XhsQuoteResponse>('/xhs-diagnosis/quote', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export type XhsRunRequest = XhsQuoteRequest & {
  tier: string;
  sample_size?: number;
  idempotency_key?: string;
};

export async function xhsCreateRun(body: XhsRunRequest) {
  return api<{ ok: boolean; run_id: number; credit_charged: number; target_id: number; resumed?: boolean }>(
    '/xhs-diagnosis/runs',
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export type XhsRun = {
  id: number;
  tier: string;
  status: string;
  sample_size: number;
  pgy_available: number;
  estimated_cost_usd: number;
  actual_cost_usd: number;
  credit_charged: number;
  error_summary: string;
  ruleset_version: string;
  created_at: string;
  completed_at: string | null;
  progress: Record<string, any>;
  account_name?: string;
  core_diagnosis?: string;
};

export async function xhsGetRun(runId: number) {
  return api<{ ok: boolean; run: XhsRun; target: any }>(`/xhs-diagnosis/runs/${runId}`);
}

export async function xhsGetReport(runId: number) {
  return api<{ ok: boolean; run: any; report: any; findings: any[] }>(
    `/xhs-diagnosis/runs/${runId}/report`
  );
}

export async function xhsRetryRun(runId: number) {
  return api<{ ok: boolean; run_id: number; message: string }>(`/xhs-diagnosis/runs/${runId}/retry`, {
    method: 'POST',
  });
}

export async function xhsMyRuns() {
  return api<{ ok: boolean; runs: XhsRun[] }>('/xhs-diagnosis/runs');
}

export async function xhsTargetHistory(targetId: number) {
  return api<{ ok: boolean; target: any; runs: XhsRun[] }>(`/xhs-diagnosis/targets/${targetId}/history`);
}

export type XhsOutcome = {
  id: number;
  period_start: string | null;
  period_end: string | null;
  dm_count: number | null;
  qualified_lead_count: number | null;
  contact_added_count: number | null;
  appointment_count: number | null;
  deal_count: number | null;
  revenue_cny: number | null;
  repurchase_count: number | null;
  referral_count: number | null;
};

export async function xhsGetOutcomes(targetId: number) {
  return api<{ ok: boolean; outcomes: XhsOutcome[] }>(`/xhs-diagnosis/targets/${targetId}/outcomes`);
}

export async function xhsAddOutcome(targetId: number, data: Partial<XhsOutcome>) {
  return api<{ ok: boolean; message: string }>(`/xhs-diagnosis/targets/${targetId}/outcomes`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ─── 互动数据趋势（免费指标快照） ────────────────────────────────

export type InteractionTrendPoint = {
  captured_at: string;
  digg_count: number | null;
  comment_count: number | null;
  share_count: number | null;
  collect_count: number | null;
  play_count: number | null;
  title?: string;
  platform?: string;
};

export async function getInteractionTrend(params: { video_id?: string; account_uid?: string; user_id?: number; limit?: number }) {
  const q = new URLSearchParams();
  if (params.video_id) q.set('video_id', params.video_id);
  if (params.account_uid) q.set('account_uid', params.account_uid);
  if (params.user_id && params.user_id > 0) q.set('user_id', String(params.user_id));
  if (params.limit) q.set('limit', String(params.limit));
  const query = q.toString();
  return api<{ ok: boolean; video_id: string; account_uid: string; points: InteractionTrendPoint[] }>(
    `/media/interaction-trend${query ? `?${query}` : ''}`
  );
}

// ─── 多平台账号体检（小红书/抖音/视频号，统一入口） ──────────────

export type DiagnosisPlatform = 'xiaohongshu' | 'douyin' | 'wechat';

export type DiagnosisTier = {
  tier: 'basic' | 'enhanced' | 'deep';
  name: string;
  credit_cost: number;
  price_cny: number;
  estimated_tikhub_usd: number;
  estimated_tikhub_cny: number;
  calls: Record<string, { count: number; unit_usd: number; subtotal_usd: number }>;
  description: string;
};

export type DiagnosisQuoteRequest = {
  platform: DiagnosisPlatform;
  input: string;
  primary_goal: string;
  intended_audience?: string;
  intended_positioning?: string;
  product_offer?: string;
};

export type DiagnosisQuoteResponse = {
  ok: boolean;
  platform: DiagnosisPlatform;
  account_id: string;
  input: string;
  tiers: DiagnosisTier[];
  ruleset_version: string;
  note: string;
};

export async function diagnosisQuote(body: DiagnosisQuoteRequest) {
  return api<DiagnosisQuoteResponse>('/account-diagnosis/quote', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export type DiagnosisRunRequest = DiagnosisQuoteRequest & {
  tier: string;
  sample_size?: number;
  idempotency_key?: string;
};

export async function diagnosisCreateRun(body: DiagnosisRunRequest) {
  return api<{ ok: boolean; run_id: number; credit_charged: number; target_id: number; resumed?: boolean }>(
    '/account-diagnosis/runs',
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export type DiagnosisRun = {
  id: number;
  platform: string;
  tier: string;
  status: string;
  sample_size: number;
  pgy_available: number;
  estimated_cost_usd: number;
  actual_cost_usd: number;
  credit_charged: number;
  error_summary: string;
  ruleset_version: string;
  created_at: string;
  completed_at: string | null;
  progress: Record<string, any>;
  account_name?: string;
  core_diagnosis?: string;
};

export async function diagnosisGetRun(runId: number) {
  return api<{ ok: boolean; run: DiagnosisRun; target: any }>(`/account-diagnosis/runs/${runId}`);
}

export async function diagnosisGetReport(runId: number) {
  return api<{ ok: boolean; run: any; report: any; findings: any[]; raw?: any }>(
    `/account-diagnosis/runs/${runId}/report`
  );
}

export async function diagnosisRetryRun(runId: number) {
  return api<{ ok: boolean; run_id: number; message: string }>(`/account-diagnosis/runs/${runId}/retry`, {
    method: 'POST',
  });
}

export async function diagnosisMyRuns() {
  return api<{ ok: boolean; runs: DiagnosisRun[] }>('/account-diagnosis/runs');
}

export async function diagnosisTargetHistory(targetId: number) {
  return api<{ ok: boolean; target: any; runs: DiagnosisRun[] }>(`/account-diagnosis/targets/${targetId}/history`);
}

export async function diagnosisGetOutcomes(targetId: number) {
  return api<{ ok: boolean; outcomes: any[] }>(`/account-diagnosis/targets/${targetId}/outcomes`);
}

export async function diagnosisAddOutcome(targetId: number, data: Record<string, any>) {
  return api<{ ok: boolean; message: string }>(`/account-diagnosis/targets/${targetId}/outcomes`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function diagnosisPlatforms() {
  return api<{ ok: boolean; platforms: { value: string; label: string }[] }>('/account-diagnosis/platforms');
}
// ─── 社媒监控：账号分析 & 爆款抓取（2026-09-10）───────────────

export type AnalysisKeyword = { word: string; count: number };
export type AnalysisTag = { tag: string; count: number };

export async function getAccountAnalysis(accountId: string | number) {
  return api<{ account_id: number; keywords: AnalysisKeyword[]; tags: AnalysisTag[]; source_count: number; analyzed_at: string | null }>(
    `/media/accounts/${accountId}/analysis`
  );
}

export async function getKeywordsSummary() {
  return api<{
    keywords: AnalysisKeyword[];
    total_keywords: number;
    total_accounts: number;
    updated_at: string | null;
  }>('/media/keywords-summary');
}

// ─── LLM 标签提取（2026-09-15）─────────────────────────────────
export async function extractTagsLlm() {
  return api<{ status: string; job_id: string; videos_total: number; message?: string }>(
    '/media/tags/extract-llm',
    { method: 'POST' },
  );
}

export async function extractTagsLlmStatus(jobId: string) {
  return api<{ status: string; videos_done?: number; videos_total?: number; message?: string; error?: string; failed?: number }>(
    `/media/tags/extract-llm/status/${jobId}`,
  );
}

export type ViralItem = {
  item_id?: string;
  note_id?: string;
  title: string;
  desc?: string;
  match_reasons?: string[];
  match_tier?: string;
  platform: string;
  nickname?: string;
  author?: string;
  author_id?: string;
  likes: number | null;
  fans?: number | null;
  play_count?: number | null;
  comments?: number | null;
  collected?: number | null;
  shared?: number | null;
  cover?: string;
  thumbnail?: string;
  url?: string;
  duration?: number;
  create_time?: number;
  type?: string;
  is_low_fans_but_hot?: boolean;
  is_high_play_over?: boolean;
};

export type ViralFetchBody = {
  platform: 'douyin' | 'xiaohongshu';
  keyword: string;
  description?: string;
  request_id?: string;
  count: number;
  date_type?: number;
  label_type?: number;
  duration_type?: number;
  sort_type?: string;
  note_type?: string;
  time_filter?: string;
};

export type ViralFetchResult = {
  items: ViralItem[]; review_items?: ViralItem[]; rejected_count?: number; fetched: number; saved: number; message: string;
  job_id: string; intent_summary?: string; warnings?: string[];
  diagnostics?: {candidate_count: number; qualified_count: number; search_calls: number; stop_reason: string; partial: boolean; degraded?: boolean; degraded_groups?: string[]; degraded_queries?: string[]};
};

export async function viralFetch(body: ViralFetchBody, signal?: AbortSignal) {
  return api<ViralFetchResult>('/media/viral/fetch', {
    method: 'POST', body: JSON.stringify(body), signal,
  });
}

export async function viralImport(job_id: string, item_ids: string[]) {
  return api<{ saved: number; total: number; failed?: number; duplicated?: number }>('/media/viral/import', {
    method: 'POST', body: JSON.stringify({ job_id, item_ids }),
  });
}

export async function viralTranscribe(file: Blob, signal: AbortSignal) {
  const form = new FormData();
  const ext = file.type.includes('mp4') ? 'm4a' : file.type.includes('ogg') ? 'ogg' : 'webm';
  form.append('file', file, `audio.${ext}`);
  form.append('request_id', crypto.randomUUID());
  const token = getToken();
  const response = await fetch('/api/media/viral/transcribe', {
    method: 'POST', body: form, signal,
    headers: token ? {Authorization: `Bearer ${token}`} : {},
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 402) notifyCreditInsufficient('语音识别积分不足');
    throw new Error(typeof data?.detail === 'string' ? data.detail : `语音识别失败 (${response.status})`);
  }
  if (typeof data?.text !== 'string' || !data.text.trim()) throw new Error('未识别到文字，请重新录音');
  return data as {text: string};
}

// ─── 积分消耗日志 ──────────────────────────────────────────────

export type CreditTransaction = {
  id: number;
  type: string;
  type_label: string;
  amount: number;
  balance_after: number;
  item: string;
  detail: string;
  created_at: string;
};

export async function getCreditTransactions(page = 1, pageSize = 20) {
  return api<{ items: CreditTransaction[]; total: number; page: number; page_size: number }>(
    `/credit/transactions?page=${page}&page_size=${pageSize}`
  );
}

// ─── 内容视频下载（爆款加载视频按钮）────────────────────────────

export async function triggerVideoDownload(videoId: string, userId?: number) {
  const q = userId ? `?user_id=${userId}` : '';
  return api<{ status: string; video_id: string }>(`/media/videos/${videoId}/download${q}`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function getVideoDownloadStatus(videoId: string, userId?: number) {
  const q = userId ? `?user_id=${userId}` : '';
  return api<{ video_id: string; download_status: string; file_size: number; downloaded: boolean; platform: string }>(
    `/media/videos/${videoId}/status${q}`
  );
}

export async function testLlmConfig(modelId: string) {
  return api<{ok: boolean; message: string; code?: string}>('/config/llm/test', {
    method: 'POST', body: JSON.stringify({model_id: modelId}),
  });
}
